/* eslint-disable jsx-a11y/label-has-for */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { CampaignSelector } from '@bingads-webui-campaign-react/campaign-selector';
import { campaignMultiSelectorWithoutDropdownStyle } from '@bingads-webui-campaign-react/common-styles';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import jsdata from '@bingads-webui-component-legacy/js-data-campaign/index';
import { buildMergedI18n } from '@bingads-webui-campaign-react/portfolio-bid-strategy-shared';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { Error } from './error';
import { i18nData } from '../i18n-map/campaign-selector-i18n';
import { AdjustmentType, isDataExclusion } from '../adjustment-type';

const PAGE_SIZE = 20;

function getStyles(dummy, { palette = {} }) {
  const textOverflowStyle = {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  };

  return {
    campaignSelector: {
      ...campaignMultiSelectorWithoutDropdownStyle({ palette }),
      '& .ba-campaign-selector .multi-selector-content > .ba-entity-selector': {
        minWidth: 600,
      },
      marginTop: 8,
      marginLeft: 30,
      marginBottom: 20,
      position: 'relative',

      '& .error': {
        marginTop: 5,
      },
    },
    rowItem: {
      display: 'flex',
      alignItems: 'center',
      padding: 8,
      maxWidth: 'unset !important',
    },
    campaignNameColumn: {
      width: 300,
      marginRight: 10,
      ...textOverflowStyle,
    },
    campaignTypeColumn: {
      width: 130,
      marginRight: 10,
      ...textOverflowStyle,
    },
    arrowIcon: {
      marginLeft: '0 !important',
      marginRight: '0 !important',
    },
    selectionCount: {
      position: 'absolute',
      top: 36,
      left: 9,
      background: '#fff',
      pointerEvents: 'none',
    },
  };
}

function getErrorMessage(selectedCampaigns) {
  if (_.isEmpty(selectedCampaigns)) {
    return _TL_('Campaign is required. Select one or more campaigns.');
  }

  return _TL_('');
}

export function validator(selectedCampaigns) {
  return _.isEmpty(getErrorMessage(selectedCampaigns));
}

function renderHeaderFactory({ classes, i18n, isSelected }) {
  return function renderHeader(itemCount) {
    return (
      <div className={`multi-item-container ${classes.rowItem}`}>
        <div className={classes.campaignNameColumn}>
          <b>
            {`${i18n.getString(isSelected ? _TL_('Selected campaigns:') : _TL_('Campaigns:'))} ${itemCount}`}
          </b>
        </div>
        <div className={classes.campaignTypeColumn}>
          <b>
            {i18n.getString(_TL_('Campaign type'))}
          </b>
        </div>
      </div>
    );
  };
}

function renderItemFactory({
  classes, i18n, isSelected, permissions,
}) {
  return function renderItem(item, index, forwardedRef) {
    const { isAddedToResultList } = item;
    const rowItemClass = isAddedToResultList ? 'added-to-result-list' : '';
    const addIconClass = isAddedToResultList ? 'disabled' : 'enabled';
    const CampaignTypeMapping = {
      Default: 'Search',
      Shopping: 'Shopping',
    };
    if (permissions.dynamic.PerformanceMaxCampaigns) {
      CampaignTypeMapping.PerformanceMax = 'PerformanceMax';
    }
    if (permissions.IsDataExclusionForAudienceCampaignEnabled) {
      CampaignTypeMapping.Audience = 'Audience';
    }
    const campaignType = i18n.getString(_TL_(CampaignTypeMapping[item.CampaignType]));

    return (
      <div className={`multi-item-container ${rowItemClass} ${classes.rowItem}`}>
        <div className={classes.campaignNameColumn} ref={forwardedRef}>
          {item.name}
        </div>
        <div className={classes.campaignTypeColumn}>
          {campaignType}
        </div>
        {isSelected ?
          <span className={`arrow-button a11y-k-listview-button remove remove-arrow-button ${classes.arrowIcon}`} /> :
          <span className={`arrow-button a11y-k-listview-button add-arrow-button ${addIconClass} ${classes.arrowIcon}`} />
        }
      </div>
    );
  };
}

function SeasonalityCampaignSelector(props) {
  const {
    classes,
    selectedCampaigns,
    onSelectCampaigns,
    showValidationError,
    i18n,
    adjustmentType,
    permissions,
  } = props;

  const selectorRef = useRef(null);

  const errorMessage = showValidationError ? i18n.getString(getErrorMessage(selectedCampaigns)) : '';

  const mergedI18n = buildMergedI18n({ i18n, legacyStringMap: i18nData });

  const campaignType = ["Enum.CampaignType'Default'", "Enum.CampaignType'Shopping'"];

  if (permissions.dynamic.PerformanceMaxCampaigns) {
    campaignType.push("Enum.CampaignType'PerformanceMax'");
  }

  if (permissions.IsDataExclusionForAudienceCampaignEnabled && isDataExclusion(adjustmentType)) {
    campaignType.push("Enum.CampaignType'Audience'");
  }

  const whereClause = {
    CampaignType: {
      $in: campaignType,
    },
  };

  function fetchCampaigns({
    pageNumber,
    pageSize,
    query,
  }) {
    const odataParams = {
      select: ['Id', 'Name', 'CampaignType'],
      limit: pageSize,
      offset: pageNumber * pageSize,
      where: whereClause,
    };

    if (query) {
      odataParams.where = {
        $and: [
          odataParams.where,
          {
            Name: { $contains: query },
          },
        ],
      };
    }

    return jsdata.Campaign.findAll(odataParams)
      .then((cmps) => {
        const { totalCount } = cmps;
        const items = _.map(cmps, cmp => ({
          Id: cmp.Id,
          name: cmp.Name,
          CampaignType: cmp.CampaignType,
        }));

        return {
          items,
          totalCount,
        };
      })
      .catch(() =>
      ({
        items: [],
        totalCount: 0,
      }));
  }

  function onSelectDone(items) {
    onSelectCampaigns(items);
  }

  function onSelectCancel() {
    // Nothing to do
  }

  return (
    <div className={`seasonality-campaign-selector ${classes.campaignSelector}`}>
      <CampaignSelector
        pageSize={PAGE_SIZE}
        i18n={mergedI18n}
        fetch={fetchCampaigns}
        isMultiMode
        onSelect={_.noop}
        onSelectDone={onSelectDone}
        onSelectCancel={onSelectCancel}
        selectedItemsForMultiMode={selectedCampaigns}
        originCampaignSelectorOptions={{
          renderHeader: renderHeaderFactory({ classes, i18n, isSelected: false }),
          renderItem: renderItemFactory({ classes, i18n, isSelected: false, permissions }),
        }}
        resultContainerOptions={{
          isHideSaveBar: false,
          renderHeader: renderHeaderFactory({ classes, i18n, isSelected: true }),
          renderItem: renderItemFactory({ classes, i18n, isSelected: true, permissions }),
        }}
        stringKeys={{
          listHeader: _TL_('Available campaigns: {{itemCount}}'),
          selectEntities: _TL_('Select campaigns'),
          emptyListContent: _TL_('No results to display.'),
          resultHeader: _TL_('Selected campaigns: {{itemCount}}'),
          actionBar: _TL_('Select all on this page'),
        }}
        hideStatus
        getCustomDropdownText={
          () => (selectedCampaigns.length > 0 ?
            i18n.getString(_TL_('{{count}} campaign(s) selected'), { count: selectedCampaigns.length }) : i18n.getString(_TL_('Select campaigns')))
        }
        ref={selectorRef}
      />
      <Error message={errorMessage} />
    </div>
  );
}


SeasonalityCampaignSelector.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  selectedCampaigns: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSelectCampaigns: PropTypes.func.isRequired,
  adjustmentType: PropTypes.oneOf([AdjustmentType.DataExclusion, AdjustmentType.SeasonalityAdjustment]).isRequired,
  showValidationError: PropTypes.bool,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      PerformanceMaxCampaigns: PropTypes.bool,
    }),
  }).isRequired,
};

SeasonalityCampaignSelector.defaultProps = {
  showValidationError: PropTypes.false,
};

const Wrapped = _.compose(
  React.memo,
  Comp => withDefaultStyles(Comp, getStyles),
  withPermissions
)(SeasonalityCampaignSelector);

export { Wrapped as SeasonalityCampaignSelector };
